import {
  type AiStatus,
  type ConnectionStatus,
  defaultRules,
  type HolidayRegion,
  type UserRules,
  type UserSettings,
  type WeekdayKey,
  weekdayKeys,
} from '@hugo/core'
import { formatHours, formatMinutes, formatUsd, relativeTime } from './format'
import { ESTIMATE_RULES } from './rules'

/** The settings overview (`/settings`): its groups, and one page per section. */
export type SettingsGroup = 'plan' | 'connected' | 'you'

export const SETTINGS_GROUPS: { id: SettingsGroup; title: string }[] = [
  { id: 'plan', title: 'How hugo plans' },
  { id: 'connected', title: 'Connected' },
  { id: 'you', title: 'You' },
]

export type SettingsSection = {
  /** Its page: `/settings/<id>`. */
  id: string
  title: string
  group: SettingsGroup | 'footer'
  /** What it holds, so a search finds it by more than its name. */
  keywords: string[]
}

export const SETTINGS_SECTIONS: readonly SettingsSection[] = [
  {
    id: 'working-time',
    title: 'Working time',
    group: 'plan',
    keywords: ['hours', 'days', 'holidays', 'time zone', 'week start', 'hours a day', 'planning'],
  },
  {
    id: 'time-off',
    title: 'Time off',
    group: 'plan',
    keywords: ['vacation', 'holiday', 'away', 'out of office', 'absence'],
  },
  {
    id: 'rules',
    title: 'Rules',
    group: 'plan',
    keywords: ['priority', 'reminder', 'time needed', 'estimate', 'focus', 'notice', 'default'],
  },
  { id: 'tags', title: 'Tags', group: 'plan', keywords: ['personal', 'family', 'label'] },
  {
    id: 'apps',
    title: 'Apps',
    group: 'connected',
    keywords: ['connections', 'gmail', 'calendar', 'chat', 'drive', 'google', 'sync', 'connect'],
  },
  {
    id: 'ai',
    title: 'hugo’s AI',
    group: 'connected',
    keywords: ['ai', 'suggestions', 'auto-file', 'filing', 'budget', 'cost'],
  },
  {
    id: 'claude-code',
    title: 'Claude Code',
    group: 'connected',
    keywords: ['mcp', 'token', 'access', 'agent', 'claude'],
  },
  { id: 'voice', title: 'Voice', group: 'you', keywords: ['language', 'dictation', 'speech'] },
  {
    id: 'account',
    title: 'Account',
    group: 'you',
    keywords: ['sign out', 'email', 'name', 'profile'],
  },
  { id: 'about', title: 'About hugo', group: 'footer', keywords: ['version', 'test', 'clock'] },
]

export const isSectionId = (id: string): boolean => SETTINGS_SECTIONS.some((s) => s.id === id)

/** Whether a section answers a search (its name, what it holds, or its summary now). */
export function matchesSearch(section: SettingsSection, query: string, summary: string): boolean {
  const q = query.trim().toLowerCase()
  if (!q) return true
  return [section.title, summary, ...section.keywords].some((text) =>
    text.toLowerCase().includes(q),
  )
}

const DAY_NAMES: Record<WeekdayKey, string> = {
  mon: 'Mon',
  tue: 'Tue',
  wed: 'Wed',
  thu: 'Thu',
  fri: 'Fri',
  sat: 'Sat',
  sun: 'Sun',
}

export const REGION_LABELS: Record<HolidayRegion, string> = {
  none: 'No holidays (weekends only)',
  CH: 'Switzerland (federal)',
  'CH-ZH': 'Zurich',
  'CH-BE': 'Bern',
  'CH-BS': 'Basel-Stadt',
  'CH-GE': 'Geneva',
  'CH-VD': 'Vaud',
  'CH-LU': 'Lucerne',
  'CH-SG': 'St. Gallen',
  'CH-AG': 'Aargau',
  'CH-TI': 'Ticino',
  DE: 'Germany',
  AT: 'Austria',
}

/** "Mon–Fri" for a run of three or more days, else "Mon, Wed, Fri". */
function daysLabel(days: WeekdayKey[]): string {
  if (days.length === 0) return 'No working days'
  const order = weekdayKeys
  const indexes = days.map((d) => order.indexOf(d))
  const contiguous = indexes.every((i, n) => n === 0 || i === (indexes[n - 1] ?? -2) + 1)
  const first = days[0]
  const last = days.at(-1)
  if (contiguous && days.length >= 3 && first && last) {
    return `${DAY_NAMES[first]}–${DAY_NAMES[last]}`
  }
  return days.map((d) => DAY_NAMES[d]).join(', ')
}

/** "Mon–Fri 09:00–18:00 · Zurich · 4 h a day". */
export function workingTimeSummary(
  settings: Pick<UserSettings, 'workingHours' | 'holidayRegion' | 'planningMinutesPerDay'>,
): string {
  const days = weekdayKeys.filter((d) => settings.workingHours[d].length > 0)
  const spans = days.map((d) => {
    const windows = settings.workingHours[d]
    return `${windows[0]?.[0] ?? ''}–${windows.at(-1)?.[1] ?? ''}`
  })
  const hours = spans.length > 0 && spans.every((s) => s === spans[0]) ? ` ${spans[0]}` : ''
  const parts = [`${daysLabel(days)}${hours || (days.length > 0 ? ' · hours vary' : '')}`]
  parts.push(
    settings.holidayRegion === 'none' ? 'no holidays' : REGION_LABELS[settings.holidayRegion],
  )
  parts.push(`${formatHours(settings.planningMinutesPerDay)} a day`)
  return parts.join(' · ')
}

/** "hugo’s defaults", or what differs: "Review 1 h 30 min · reminders after 5 working days". */
export function rulesSummary(rules: UserRules): string {
  const changed: string[] = []
  for (const { key, label } of ESTIMATE_RULES) {
    if (rules.estimateMinutes[key] !== defaultRules.estimateMinutes[key]) {
      changed.push(`${label} ${formatMinutes(rules.estimateMinutes[key])}`)
    }
  }
  if (rules.reminderIntervalWorkingDays !== defaultRules.reminderIntervalWorkingDays) {
    changed.push(`reminders after ${rules.reminderIntervalWorkingDays} working days`)
  }
  if (rules.awaySoonDays !== defaultRules.awaySoonDays) {
    changed.push(`away warning ${rules.awaySoonDays} days ahead`)
  }
  if (
    rules.timeOffNoticeDays !== defaultRules.timeOffNoticeDays ||
    rules.undatedNoticeWorkingDays !== defaultRules.undatedNoticeWorkingDays
  ) {
    changed.push('time-off notices changed')
  }
  if (rules.focusBlockMinutes !== defaultRules.focusBlockMinutes) {
    changed.push(`focus from ${formatMinutes(rules.focusBlockMinutes)}`)
  }
  return changed.length === 0 ? 'hugo’s defaults' : changed.join(' · ')
}

/** "3 tags · #family is personal". */
export function tagsSummary(tags: readonly string[], personal: readonly string[]): string {
  if (tags.length === 0) return 'No tags yet'
  const count = tags.length === 1 ? '1 tag' : `${tags.length} tags`
  const mine = personal.filter((t) => tags.includes(t))
  if (mine.length === 0) return count
  return `${count} · ${mine.map((t) => `#${t}`).join(', ')} ${mine.length === 1 ? 'is' : 'are'} personal`
}

/** "Gmail connected · Google Calendar needs attention". */
export function appsSummary(
  connected: readonly { title: string; status: ConnectionStatus }[],
): string {
  const active = connected.filter((c) => c.status === 'active').map((c) => c.title)
  const failing = connected.filter((c) => c.status === 'error').map((c) => c.title)
  if (active.length === 0 && failing.length === 0) return 'Nothing connected yet'
  const parts: string[] = []
  if (active.length > 0) parts.push(`${active.join(', ')} connected`)
  if (failing.length > 0) parts.push(`${failing.join(', ')} needs attention`)
  return parts.join(' · ')
}

/** "Files clear asks for you · $0.40 of $2.00 today". */
export function aiSummary(status: Pick<AiStatus, 'enabled' | 'autoFile' | 'today'>): string {
  if (!status.enabled) return 'Off on this server'
  const what = status.autoFile ? 'Files clear asks for you' : 'Suggests where asks belong'
  return `${what} · ${formatUsd(status.today.costUsd)} of ${formatUsd(status.today.budgetUsd)} today`
}

/** "2 tokens · last used 2 hours ago", or "Not connected". */
export function tokensSummary(tokens: readonly { lastUsedAt: string | null }[], now: Date): string {
  if (tokens.length === 0) return 'Not connected'
  const count = tokens.length === 1 ? '1 token' : `${tokens.length} tokens`
  const last = tokens
    .map((t) => t.lastUsedAt)
    .filter((t): t is string => t !== null)
    .sort()
    .at(-1)
  return last
    ? `${count} · last used ${relativeTime(new Date(last), now)}`
    : `${count} · not used yet`
}
